import { BaseGridDropListComponent } from '@anecdotes/design-system/drag-drop';
import { IS_MOBILE_TOKEN } from '@anecdotes/design-system/injection-tokens';
import { LoaderManagerService } from '@anecdotes/design-system/loaders';
import { ModalWindowService } from '@anecdotes/design-system/modals';
import {
  DownloadEvidenceParams,
  DownloadingResource,
  OrderResourceType,
  PolicyResponse,
  TrustCenterEventService,
  TrustCenterFacadeService,
  TrustCenterResourceTypes,
  TrustResourceEventDataProperty,
} from '@anecdotes/shared-trust-center';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, DestroyRef, Inject, OnDestroy, OnInit, SkipSelf } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Observable, firstValueFrom, shareReplay } from 'rxjs';
import { RegularDateFormatMMMdyyyy } from '@anecdotes/helpers';
import { AppRoutes } from '@anecdotes/configuration-data-access';
import { getCurrentlyVisibleObjects } from '../../../../utils';
import { PolicyEditSidePanelComponent } from '../policy-edit-side-panel/policy-edit-side-panel.component';
import { PolicySidePanelComponent } from '../policy-side-panel/policy-side-panel.component';

@Component({
  selector: 'app-policies',
  templateUrl: './policies.component.html',
  styleUrls: ['./policies.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PoliciesComponent extends BaseGridDropListComponent implements OnInit, OnDestroy {
  protected readonly DownloadingResource = DownloadingResource;
  protected readonly dateFormat = RegularDateFormatMMMdyyyy;

  editing$: Observable<boolean>;
  isPreviewMode$: Observable<boolean>;
  policies: PolicyResponse[];

  constructor(
    @SkipSelf() private loaderManager: LoaderManagerService,
    private router: Router,
    private modalWindowService: ModalWindowService,
    private trustCenterFacadeService: TrustCenterFacadeService,
    private destroyRef: DestroyRef,
    private readonly trustCenterEventService: TrustCenterEventService,
    @Inject(IS_MOBILE_TOKEN) readonly isMobile: boolean,
  ) {
    super();
  }

  ngOnInit(): void {
    this.editing$ = this.trustCenterFacadeService.isEditing();
    this.isPreviewMode$ = this.trustCenterFacadeService.isPreviewMode().pipe(shareReplay({ bufferSize: 1, refCount: true }));

    getCurrentlyVisibleObjects(this.editing$, this.trustCenterFacadeService.getPolicies(), 'position')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((policies) => {
        this.policies = policies;
        this.cd.detectChanges();
      });
  }

  buildTranslationKey(key: string): string {
    return `trustCenter.${key}`;
  }

  updateOrder(sourceIndex: number, targetIndex: number): void {
    moveItemInArray(this.policies, sourceIndex, targetIndex);

    const orderList = this.policies.map((policy) => policy.id);
    this.trustCenterFacadeService.reorderResources(OrderResourceType.Policy, orderList);
  }

  ngOnDestroy(): void {
    this.loaderManager.hide();
  }

  goToPolicyManager(): void {
    this.router.navigate([AppRoutes.PolicyManager]);
  }

  openPolicyEditSidepanel(policy: PolicyResponse): void {
    this.modalWindowService.close();

    this.modalWindowService.openMediumSlidePanel({
      componentsToSwitch: [
        {
          id: 'app-policy-edit-side-panel',
          componentType: PolicyEditSidePanelComponent,
          contextData: {
            policy,
          },
        },
      ],
      options: {
        closeOnEsc: true,
        reOpenOnClose: true,
        onClose: (cleanup) => {
          if (!cleanup) {
            this.trustCenterFacadeService.clearDraft(TrustCenterResourceTypes.Policy, policy.id);
          }
        },
      },
    });
  }

  openPolicySidepanel(policy: PolicyResponse): void {
    this.modalWindowService.close();

    const componentsToSwitch = [
      {
        id: 'app-policy-side-panel',
        componentType: PolicySidePanelComponent,
        contextData: {
          policy,
        },
      },
    ];

    this.modalWindowService.openMediumSlidePanel({
      id: 'policy-side-panel',
      componentsToSwitch,
      options: {
        closeOnEsc: true,
        reOpenOnClose: true,
      },
    });
  }

  async onPolicyClick(policy: PolicyResponse): Promise<void> {
    const editing = await firstValueFrom(this.editing$);
    const isPreviewMode = await firstValueFrom(this.isPreviewMode$);

    if (editing) {
      this.openPolicyEditSidepanel(policy);
    } else {
      if (!isPreviewMode) {
        const companyName = await firstValueFrom(this.trustCenterFacadeService.getName());
        this.trustCenterEventService.trackClickOnResourceItem(TrustResourceEventDataProperty.Policies, policy.id, companyName);
      }

      this.openPolicySidepanel(policy);
    }
  }

  getEvidence(policy: PolicyResponse): DownloadEvidenceParams[] {
    return [
      {
        ...policy.evidence_files[0],
        evidenceInstanceId: policy.policy_approved_instance,
      },
    ];
  }
}
